import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { DataTableCallbackRefs, DataTableOperations, GridMachineContext, QueueDelete } from "@iventis/data-table";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { toast } from "@iventis/toasts/src/toast";
import { useTheme } from "@mui/material";
import { Theme } from "@emotion/react";
import { greyTheme } from "@iventis/styles";
import { RequestAggregator } from "@iventis/api-helpers/src/request-aggregator";
import { scheduleApi } from "@iventis/api/src/api";
import { toastError, toastSuccess } from "@iventis/data-table/helpers/data-table-toasts";
import { DataTable } from "@iventis/data-table/components/data-table";
import { ScheduleMachineContext } from "./schedule.machine.types";
import { editSingleCell, generateColumnsForScheduleTable, generateRowsForScheduleTable } from "./schedule-table.helpers";
import { deleteEventBlock, EventBlockPutResponse } from "./schedule-api-functions";
import { ScheduleTableRowNode, ScheduleTableData } from "./schedule-table.types";

// Memoize the entire component as once this is rendered, we don't want to re-render.
// This is because when we make edits to the data table, we send the results to the machine.
// The machine then updates it's context triggering a re-rendering (causing a flickering effect).
// The data table however has it's own local state with the updated values already, so doesn't require this re-render
export const ScheduleTable: React.FC<{
    machineContext: ScheduleMachineContext;
    updateEventBlock: (eventBlocks: EventBlockPutResponse) => void;
    deleteEventBlock: (id: string) => void;
    showEventUnits: boolean;
    timezone: string;
}> = memo(
    ({ machineContext, updateEventBlock: updateEventBlockToMachine, deleteEventBlock: deleteEventBlockToMachine, showEventUnits, timezone }) => {
        const translate = useIventisTranslate();
        const ref = useRef<DataTableCallbackRefs>();

        const [tableKey, setTableKey] = useState(showEventUnits ? "event-units-table" : "sessions-table");
        const theme = useTheme<Theme>();

        useEffect(() => {
            setTableKey(showEventUnits ? "event-units-table" : "sessions-table");
        }, [showEventUnits]);

        // Refreshes the data table state
        const refreshCells = () => setTableKey(`${tableKey}0`);

        const listItemRequestAggregator = new RequestAggregator<DataFieldListItem[], string>(scheduleApi, (id) => `/data-field-list-items/${id}`);

        const columns = useMemo(
            () =>
                generateColumnsForScheduleTable(
                    machineContext,
                    translate,
                    showEventUnits,
                    listItemRequestAggregator.get.bind(listItemRequestAggregator) as typeof listItemRequestAggregator.get
                ),
            [machineContext, showEventUnits]
        );

        const rows = useMemo(() => generateRowsForScheduleTable(machineContext, showEventUnits, timezone), [machineContext, showEventUnits]);

        // eslint-disable-next-line react/destructuring-assignment
        if (machineContext.eventBlockHierarchy.children.length === 0) {
            return null;
        }

        const editRow = async (context: GridMachineContext) => {
            const queue = context.queue[0];
            const { node, update } = queue.operation === DataTableOperations.Edit && queue;
            const [columnId, newValue] = update;
            const sessionResponse = await editSingleCell(node as ScheduleTableRowNode, columnId, machineContext, newValue);
            updateEventBlockToMachine(sessionResponse);
        };

        return (
            <DataTable
                // This key is required because we want to show a different table entirely for each event type,
                // so when the event type changes we can flush away any state held within ag-grid
                key={tableKey}
                ref={ref}
                theme={greyTheme(theme)}
                gridOptions={{
                    rowData: rows,
                    headerHeight: 30,
                    rowHeight: 36,
                    suppressColumnVirtualisation: true,
                    stopEditingWhenCellsLoseFocus: false,
                    context: { translate, timezone, toast },
                }}
                defaultSort={[
                    {
                        colId: "startAt",
                        sort: "asc",
                        sortIndex: 0,
                    },
                ]}
                columns={columns}
                editRowAsync={editRow}
                deleteRowAsync={async (context) => {
                    const queue = context.queue[0] as QueueDelete;
                    const { row } = queue;
                    const { id, type } = row as ScheduleTableData;
                    await deleteEventBlock(id, type);
                    deleteEventBlockToMachine(id);
                    return row;
                }}
                onNetworkRequestSuccess={toastSuccess}
                onNetworkRequestError={(context) => toastError(context, refreshCells)}
            />
        );
    },
    (prev, next) => prev.showEventUnits === next.showEventUnits
);
